
import { Plus, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { useEnhancedTranslation } from "@/hooks/useEnhancedTranslation";

export function EmptyContractsState() {
  const { t, tElement } = useEnhancedTranslation();

  const steps = [
    {
      title: tElement("contracts.emptyState.step1.title", "Enter Basic Details"),
      description: tElement(
        "contracts.emptyState.step1.description",
        "Start by entering the supplier name and basic contract information."
      ),
    },
    {
      title: tElement("contracts.emptyState.step2.title", "Set Terms"),
      description: tElement(
        "contracts.emptyState.step2.description",
        "Define contract terms including expiry date and expected costs."
      ),
    },
    {
      title: tElement("contracts.emptyState.step3.title", "Add Documents"),
      description: tElement(
        "contracts.emptyState.step3.description",
        "Upload any relevant contract documents and files."
      ),
    },
  ];

  return (
    <div className="flex flex-col items-center justify-center p-8 text-center space-y-8 max-w-3xl mx-auto">
      <div className="space-y-4">
        <h2 className="text-3xl font-bold tracking-tight">
          {tElement(
            "contracts.emptyState.title",
            "Get Started with Contract Management"
          )}
        </h2>
        <p className="text-muted-foreground max-w-[600px] mx-auto">
          {tElement(
            "contracts.emptyState.description",
            "Create your first contract to start organizing and managing your business agreements effectively."
          )}
        </p>
      </div>

      <Button asChild size="lg" className="animate-bounce">
        <Link to="/contracts/quick-add">
          <Plus className="mr-2 h-5 w-5" />
          {tElement("contracts.emptyState.addFirstContract", "Quickly add your first contract")}
        </Link>
      </Button>

      <div className="grid gap-6 md:grid-cols-3 w-full mt-8">
        {steps.map((step, index) => (
          <Card key={index} className="transform transition-transform hover:scale-105">
            <CardContent className="pt-6">
              <div className="rounded-full bg-primary/10 w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-primary">{index + 1}</span>
              </div>
              <h3 className="font-semibold mb-2">{step.title}</h3>
              <p className="text-sm text-muted-foreground">{step.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-center gap-2 text-muted-foreground mt-6">
        <HelpCircle className="h-4 w-4" />
        <span>
          {tElement(
            "contracts.emptyState.needHelp",
            "Need help? Check our "
          )}{" "}
          <a
            href="https://docs.lovable.dev/getting-started"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            {tElement("contracts.emptyState.documentation", "documentation")}
          </a>
        </span>
      </div>
    </div>
  );
}
